<!-- packages/studio/src/components/army/EnhancementTableEditor.vue -->
<!-- Edits one enhancement table (IEnhancementGroup): a named sub-table within a
     category (Artefacts of Power / Heroic Traits / …) holding a restriction and
     the enhancement upgrades it offers, each a single-ability upgrade. -->
<template>
  <div class="table-editor">
    <TitleBar
      :name="modelValue.name"
      noun="Table"
      @rename="$emit('rename', $event)"
      @duplicate="$emit('duplicate')"
      @delete="$emit('delete')"
    >
      <template #tabs>
        <StSegmentedControl v-model="tab" :options="tabs" size="sm" on-shell />
      </template>
    </TitleBar>

    <div class="table-editor__pane">
      <template v-if="tab === 'editor'">
        <div class="table-editor__head">
          <StField label="Restriction" hint="Set from the category's keyword filter">
            <StTextarea :model-value="modelValue.restrictionText ?? ''" disabled />
          </StField>
          <StField label="Scourge" hint="Seasonal rules set">
            <StSelect
              :model-value="modelValue.scourge"
              @update:model-value="patch({ scourge: $event as ScourgeType })"
            >
              <option v-for="o in scourgeOptions" :key="o.value" :value="o.value">
                {{ o.label }}
              </option>
            </StSelect>
          </StField>
        </div>

        <section class="form-section">
          <div class="st-eyebrow">Enhancements</div>
          <EditableList
            :items="modelValue.upgrades"
            add-label="Add enhancement"
            remove-label="Remove enhancement"
            @add="addUpgrade"
            @remove="removeUpgrade"
          >
            <template #row="{ index }">
              <!-- The upgrade name mirrors its ability name (never shown
                   separately on the frontend), so the name field is hidden and
                   kept in sync in patchUpgrade. -->
              <UpgradeGroupForm
                single-ability
                hide-name
                hide-rules
                show-points
                :model-value="modelValue.upgrades[index]"
                @update:model-value="patchUpgrade(index, $event)"
              />
            </template>
          </EditableList>
        </section>
      </template>

      <div v-else class="table-preview">
        <div
          v-if="modelValue.restrictionText"
          class="table-preview__restriction"
          v-html="formatText(modelValue.restrictionText)"
        ></div>
        <GroupPreview
          v-for="u in modelValue.upgrades"
          :key="u.id"
          :group="u"
          hide-name
          show-points
        />
        <p v-if="!modelValue.upgrades.length" class="table-preview__empty">No enhancements yet.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { IEnhancementGroup, IUpgrade } from '@scrolldex/shared';
import { makeUpgrade, ScourgeType } from '@scrolldex/shared';
import TitleBar from '../editor/TitleBar.vue';
import {
  StSegmentedControl,
  StField,
  StTextarea,
  StSelect,
  type SegmentOption,
} from '../ui/index.js';
import EditableList from '../EditableList.vue';
import UpgradeGroupForm from './UpgradeGroupForm.vue';
import GroupPreview from './GroupPreview.vue';
import { formatText } from '../../utils/formatter.js';

const props = defineProps<{ modelValue: IEnhancementGroup }>();
const emit = defineEmits<{
  (e: 'update:modelValue', value: IEnhancementGroup): void;
  (e: 'rename', next: string): void;
  (e: 'duplicate'): void;
  (e: 'delete'): void;
}>();

const tab = ref<string | number>('editor');
const tabs: SegmentOption[] = [
  { value: 'editor', label: 'Editor', icon: 'file-pen' },
  { value: 'preview', label: 'Preview', icon: 'eye' },
];

const scourgeOptions = [
  { value: ScourgeType.None, label: 'None' },
  { value: ScourgeType.SoG, label: 'Scourge of Ghyran' },
  { value: ScourgeType.SoA, label: 'Scourge of Aqshy' },
];

function patch(part: Partial<IEnhancementGroup>) {
  emit('update:modelValue', { ...props.modelValue, ...part });
}
function patchUpgrade(index: number, next: IUpgrade) {
  // Enhancement upgrade name always mirrors its single ability's name (the only
  // name the frontend ever uses), so keep it in sync as the ability is edited.
  const synced: IUpgrade = { ...next, name: next.abilities[0]?.name ?? '' };
  patch({ upgrades: props.modelValue.upgrades.map((u, i) => (i === index ? synced : u)) });
}
function addUpgrade() {
  patch({ upgrades: [...props.modelValue.upgrades, makeUpgrade('')] });
}
function removeUpgrade(index: number) {
  patch({ upgrades: props.modelValue.upgrades.filter((_, i) => i !== index) });
}
</script>

<style scoped>
.table-editor {
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.table-editor__pane {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-4);
  padding: var(--st-space-3);
  overflow: auto;
}
.table-editor__head {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: var(--st-space-3);
  align-items: start;
}
.form-section {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
}
.table-preview {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-3);
}
.table-preview__restriction {
  font-size: var(--st-text-sm);
  font-style: italic;
  color: var(--st-text-muted);
}
.table-preview__empty {
  color: var(--st-text-muted);
  font-size: var(--st-text-sm);
}
</style>
